import type { GameRuleFieldOption, GameRuleFieldType, GameRuleWizardField } from "../types/gameRuleTemplate";

const FIELD_TYPES: ReadonlySet<string> = new Set([
  "text_input",
  "text_area",
  "checkbox",
  "radio",
  "select",
  "date_picker",
  "number_input"
]);

const needsOptions = (t: GameRuleFieldType): boolean =>
  t === "checkbox" || t === "radio" || t === "select";

const asString = (v: unknown): string => (typeof v === "string" ? v : v != null ? String(v) : "");

/**
 * Maps server `fields_json` objects into wizard field rows. Skips invalid rows (logs in dev).
 */
export const mapFieldsJsonToWizardFields = (raw: ReadonlyArray<Record<string, unknown>>): GameRuleWizardField[] => {
  const out: GameRuleWizardField[] = [];
  for (const row of raw) {
    const key = asString(row.key).trim();
    const label = asString(row.label).trim();
    const typeStr = asString(row.type).trim();
    if (!key || !label || !FIELD_TYPES.has(typeStr)) {
      if (import.meta.env.DEV) {
        console.warn("[GameRule] Skipping invalid fields_json row", row);
      }
      continue;
    }
    const type = typeStr as GameRuleFieldType;
    const idRaw = row.id;
    const id =
      typeof idRaw === "string" && idRaw.trim()
        ? idRaw.trim()
        : typeof idRaw === "number"
          ? String(idRaw)
          : crypto.randomUUID();
    const required = Boolean(row.required);
    let options: GameRuleFieldOption[] = [];
    if (needsOptions(type)) {
      const rawOpts = row.options;
      if (Array.isArray(rawOpts)) {
        options = rawOpts.map((o) => {
          if (o && typeof o === "object" && !Array.isArray(o)) {
            const rec = o as Record<string, unknown>;
            return {
              value: asString(rec.value).trim() || "option",
              label: asString(rec.label)
            };
          }
          return { value: "option", label: "" };
        });
      }
      if (options.length === 0) {
        options = [{ value: "option_1", label: "" }];
      }
    }
    out.push({ id, key, label, type, required, options });
  }
  return out;
};
